import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { user, isAuthenticated, token } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch permissions when user logs in
  useEffect(() => {
    const fetchPermissions = async () => {
      // Only fetch if authenticated AND token is set
      if (isAuthenticated && user && token) {
        try {
          // Ensure Authorization header is set (in case axios defaults haven't updated yet)
          const response = await axios.get(`${API}/permissions/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPermissions(response.data);
        } catch (error) {
          console.error("Failed to fetch permissions:", error);
          // Set default permissions if fetch fails
          setPermissions(null);
        }
      } else {
        setPermissions(null);
      }
      setLoading(false);
    };

    fetchPermissions();
  }, [isAuthenticated, user, token]);

  // Feature aliases for backward compatibility (e.g., "appointments" -> "schedule")
  const featureAliases = {
    "schedule": ["appointments", "schedule"],
    "appointments": ["schedule", "appointments"],
  };

  // Check if user has permission for a feature/action
  const hasPermission = useCallback((feature, action = null) => {
    // Admin always has all permissions
    if (user?.role === "admin") return true;
    
    if (!permissions) return false;
    
    // Check the feature and any aliases
    const featuresToCheck = featureAliases[feature] || [feature];
    
    for (const feat of featuresToCheck) {
      const featurePerm = permissions[feat];
      
      if (featurePerm === undefined) continue;
      
      // Boolean permission (like "dashboard": true)
      if (typeof featurePerm === "boolean") {
        return featurePerm;
      }
      
      // Object permission (like "clients": { "read": true, "create": false })
      if (typeof featurePerm === "object") {
        if (action) {
          if (featurePerm[action] === true) return true;
        } else {
          // If no action specified, check if ANY action is allowed
          if (Object.values(featurePerm).some(v => v === true)) return true;
        }
      }
    }
    
    return false;
  }, [permissions, user]);

  // Check if user can view a feature (shorthand)
  const canView = useCallback((feature) => hasPermission(feature, "view"), [hasPermission]);
  
  // Check if user can create in a feature
  const canCreate = useCallback((feature) => hasPermission(feature, "create"), [hasPermission]);
  
  // Check if user can edit in a feature
  const canEdit = useCallback((feature) => hasPermission(feature, "edit"), [hasPermission]);
  
  // Check if user can delete in a feature
  const canDelete = useCallback((feature) => hasPermission(feature, "delete"), [hasPermission]);

  // Refresh permissions (useful after admin changes them)
  const refreshPermissions = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const response = await axios.get(`${API}/permissions/me`);
        setPermissions(response.data);
      } catch (error) {
        console.error("Failed to refresh permissions:", error);
      }
    }
  }, [isAuthenticated]);

  const value = {
    permissions,
    loading,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    refreshPermissions,
    // Quick role check
    isAdmin: user?.role === "admin",
    isRN: user?.role === "rn",
    isCaseManager: user?.role === "case_manager",
    isPCW: user?.role === "pcw",
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
